/**
 * ImageFlyOverlay — fullscreen image overlay with Motion fly-in/out animation
 * from a source element's position. Uses Swiper for hardware-accelerated
 * touch sliding between images (Apple Photos-like behavior).
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, useReducedMotion } from 'motion/react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Virtual } from 'swiper/modules';
import type SwiperType from 'swiper';
import 'swiper/css';
import 'swiper/css/virtual';
import styles from './ImageFlyOverlay.module.css';

interface Props {
  /** Index of the currently displayed item in the items array */
  activeIndex: number;
  /** All items that can be swiped through */
  items: Array<{ url: string; videoUrl?: string }>;
  /** Source element to fly from/to — pass the DOM element ref */
  sourceElement: HTMLElement | null;
  /** Called when the overlay is dismissed */
  onClose: () => void;
  /** Called when user swipes to a different item — receives the new index */
  onIndexChange: (index: number) => void;
  /** Optional extra content rendered bottom-center (e.g., action buttons) */
  children?: React.ReactNode;
  /** Optional extra content rendered top-right alongside close button */
  topRight?: React.ReactNode;
}

function computeFlyValues(rect: DOMRect | null): { x: number; y: number; scale: number } {
  if (!rect) return { x: 0, y: 0, scale: 1 };
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const x = rect.left + rect.width / 2 - vw / 2;
  const y = rect.top + rect.height / 2 - vh / 2;
  const scale = Math.min(rect.width / vw, rect.height / vh);
  return { x, y, scale };
}

const SPRING = { type: 'spring' as const, stiffness: 300, damping: 30 };
const INSTANT = { duration: 0 };

function VideoSlide({ videoUrl, posterUrl }: { videoUrl: string; posterUrl: string }) {
  const ref = useRef<HTMLVideoElement>(null);
  const [playing, setPlaying] = useState(false);

  const toggle = useCallback(() => {
    const v = ref.current;
    if (!v) return;
    if (v.paused) {
      v.play().catch(() => {});
      setPlaying(true);
    } else {
      v.pause();
      setPlaying(false);
    }
  }, []);

  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    const onEnded = () => setPlaying(false);
    v.addEventListener('ended', onEnded);
    return () => v.removeEventListener('ended', onEnded);
  }, []);

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <video ref={ref} className={styles.image} src={videoUrl} poster={posterUrl} playsInline preload="metadata" />
      <button
        onClick={e => { e.stopPropagation(); toggle(); }}
        style={{
          position: 'absolute', width: 56, height: 56, borderRadius: '50%',
          background: 'rgba(0,0,0,0.55)', border: 'none', cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          opacity: playing ? 0 : 0.9, transition: 'opacity 0.2s',
          pointerEvents: 'auto',
        }}
      >
        <svg width="24" height="24" viewBox="0 0 24 24" fill="#fff">
          {playing
            ? <><rect x="5" y="4" width="5" height="16" rx="1" /><rect x="14" y="4" width="5" height="16" rx="1" /></>
            : <path d="M6.5 4.12v15.76a1 1 0 001.52.86l12.36-7.88a1 1 0 000-1.72L8.02 3.26a1 1 0 00-1.52.86z" />
          }
        </svg>
      </button>
    </div>
  );
}

export function ImageFlyOverlay({ activeIndex, items, sourceElement, onClose, onIndexChange, children, topRight }: Props) {
  const reducedMotion = useReducedMotion();
  const swiperRef = useRef<SwiperType | null>(null);
  const sourceRectRef = useRef<DOMRect | null>(null);

  // Capture source rect and hide source element on mount
  useEffect(() => {
    if (sourceElement) {
      sourceRectRef.current = sourceElement.getBoundingClientRect();
      sourceElement.style.visibility = 'hidden';
    }
    return () => {
      if (sourceElement) sourceElement.style.visibility = '';
    };
  }, [sourceElement]);

  // Sync Swiper to external activeIndex changes (e.g., from parent)
  useEffect(() => {
    if (swiperRef.current && swiperRef.current.activeIndex !== activeIndex) {
      swiperRef.current.slideTo(activeIndex, 0);
    }
  }, [activeIndex]);

  const handleClose = useCallback(() => {
    // Re-capture rect for exit animation
    if (sourceElement) {
      sourceRectRef.current = sourceElement.getBoundingClientRect();
    }
    if (sourceElement) sourceElement.style.visibility = '';
    onClose();
  }, [sourceElement, onClose]);

  const handleSlideChange = useCallback((swiper: SwiperType) => {
    onIndexChange(swiper.activeIndex);
  }, [onIndexChange]);

  // ── Swipe-down to dismiss ──────────────────────────────────────────
  const DISMISS_THRESHOLD = 120;
  const touchStartRef = useRef<{ y: number; x: number } | null>(null);
  const [dragY, setDragY] = useState(0);
  const isDraggingDown = useRef(false);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    touchStartRef.current = { y: touch.clientY, x: touch.clientX };
    isDraggingDown.current = false;
  }, []);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    if (!touchStartRef.current) return;
    const touch = e.touches[0];
    const dy = touch.clientY - touchStartRef.current.y;
    const dx = Math.abs(touch.clientX - touchStartRef.current.x);

    // Only start vertical drag if moving more vertically than horizontally and moving down
    if (!isDraggingDown.current) {
      if (dy > 10 && dy > dx * 1.5) {
        isDraggingDown.current = true;
      } else if (dx > 10) {
        // Horizontal swipe — let Swiper handle it
        touchStartRef.current = null;
        return;
      }
    }

    if (isDraggingDown.current && dy > 0) {
      setDragY(dy);
    }
  }, []);

  const onTouchEnd = useCallback(() => {
    if (isDraggingDown.current && dragY >= DISMISS_THRESHOLD) {
      handleClose();
    }
    touchStartRef.current = null;
    isDraggingDown.current = false;
    setDragY(0);
  }, [dragY, handleClose]);

  const dragOpacity = dragY > 0 ? Math.max(0.3, 1 - dragY / (DISMISS_THRESHOLD * 2)) : 1;
  const dragScale = dragY > 0 ? Math.max(0.85, 1 - dragY / 1500) : 1;
  // ── End swipe-down ──────────────────────────────────────────────────

  const flyInitial = reducedMotion ? { x: 0, y: 0, scale: 1 } : computeFlyValues(sourceRectRef.current);

  const getExitValues = () => {
    if (sourceElement) {
      sourceRectRef.current = sourceElement.getBoundingClientRect();
    }
    return reducedMotion ? { x: 0, y: 0, scale: 1 } : computeFlyValues(sourceRectRef.current);
  };

  return createPortal(
    <motion.div
      className={styles.overlay}
      initial={{ opacity: reducedMotion ? 1 : 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0, transition: { duration: 0.6, ease: 'easeIn' } }}
      transition={{ duration: 0.3 }}
      onClick={handleClose}
    >
      {/* Top-right buttons — vanish instantly on exit */}
      <motion.div exit={{ opacity: 0, transition: INSTANT }}>
        <div className={styles.topRight}>
          {topRight}
          <button className={styles.closeBtn} onClick={handleClose}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="4,14 4,20 10,20" />
              <polyline points="20,10 20,4 14,4" />
              <line x1="14" y1="10" x2="21" y2="3" />
              <line x1="3" y1="21" x2="10" y2="14" />
            </svg>
          </button>
        </div>
      </motion.div>

      {/* Card — fly-in/out + swipe-down dismiss */}
      <motion.div
        className={styles.swiperWrap}
        initial={flyInitial}
        animate={{ x: 0, y: dragY, scale: dragScale }}
        exit={getExitValues()}
        transition={dragY > 0 ? { duration: 0 } : (reducedMotion ? INSTANT : SPRING)}
        style={{ opacity: dragOpacity }}
        onClick={e => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
      >
        <Swiper
          modules={[Virtual]}
          virtual
          initialSlide={activeIndex}
          slidesPerView={1}
          spaceBetween={0}
          speed={400}
          onSwiper={swiper => { swiperRef.current = swiper; }}
          onSlideChange={handleSlideChange}
          style={{ width: '100%', height: '100%' }}
        >
          {items.map((item, i) => (
            <SwiperSlide key={i} virtualIndex={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {item.videoUrl
                ? <VideoSlide videoUrl={item.videoUrl} posterUrl={item.url} />
                : <img className={styles.image} src={item.url} alt="" />
              }
            </SwiperSlide>
          ))}
        </Swiper>
      </motion.div>

      {/* Action buttons — vanish instantly on exit */}
      {children && (
        <motion.div exit={{ opacity: 0, transition: INSTANT }}>
          <div className={styles.actionWrap} onClick={e => e.stopPropagation()}>
            {children}
          </div>
        </motion.div>
      )}
    </motion.div>,
    document.body,
  );
}
